/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { XCircleIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { useToken } from '../../hooks/useToken';
import { sendEvent } from '../../utilities/analytics';
import Spinner from '../common/Spinner';
import { getConfig } from '../../config/config-helper';
import { Input } from './Input';
import { fetchEvent } from '../../reducers/eventsSlice';
import { useAppDispatch } from '../../hooks';

interface Props {
  eventSlug: string;
  size?: 'medium' | 'mediumPlus' | 'large';
  className?: string;
}

export default function RejectButton({ eventSlug, size = 'medium', className = '' }: Props) {
  const dispatch = useAppDispatch();
  const token = useToken();
  const [disabled, setDisabled] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { dataopsAdminAppApiEndpoint } = getConfig();
  const url = `${dataopsAdminAppApiEndpoint}/event_management/${eventSlug}/reject`;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  async function handleReject(reason: string) {
    if (!reason.trim()) {
      setErrors(['Rejection reason is required']);
      return;
    }
    sendEvent({ name: 'event-reject-start', properties: { eventSlug } });
    setDisabled(true);
    setRejecting(true);
    setErrors([]);
    try {
      await axios.post(`${url}`, { rejection_reason: reason }, { headers: { authorization: `Bearer ${token}` } });
      setRejectionReason('');
      dispatch(fetchEvent({ token, slug: eventSlug })).catch((err: unknown) => err);
    } catch (err: any) {
      console.log('Failed to reject event: ', err);
      setErrors([err.response?.data?.message || 'Failed to reject event']);
    } finally {
      setDisabled(false);
      setRejecting(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <Input
        customRef={inputRef}
        type="text"
        value={rejectionReason}
        onChange={(e) => {
          setRejectionReason(e.target.value);
        }}
        placeholder="Enter rejection reason"
        disabled={disabled}
        error={errors.length > 0 ? errors[0] : undefined}
        loading={rejecting}
      />
      <Button
        intent="danger"
        size={size}
        className={`w-full ${className}`}
        onClick={() => {
          void handleReject(rejectionReason);
        }}
        disabled={disabled}
      >
        {rejecting ? <Spinner size="small" className="mr-1" /> : <XCircleIcon className="w-4 h-4 mr-1" />}
        Reject with reason
      </Button>
    </div>
  );
}
